import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, BookOpen, Palette, FileText, Settings, Save, Search, Upload, Volume2 } from "lucide-react";
import { loadVoices, listBrowserVoices, speak, setVoicePrefs } from "./speech";
import { SubjectSelect } from "./_pickers";

const SUBTABS = [
  { id: "read", label: "Read to Lead", Icon: BookOpen },
  { id: "art", label: "Art Studio Prompts", Icon: Palette },
  { id: "notes", label: "Notes, Lessons & Translations", Icon: FileText },
  { id: "settings", label: "PonderrLearn Core Settings", Icon: Settings },
];

export default function GlobalPonderrLearn() {
  const [sub, setSub] = useState("read");
  const [boards, setBoards] = useState([]);
  const [grades, setGrades] = useState([]);
  const [settings, setSettings] = useState({ enabled_languages: [] });
  useEffect(() => {
    api.get("/global/boards").then((r) => setBoards(r.data)).catch(() => {});
    api.get("/global/master-grades").then((r) => setGrades(r.data)).catch(() => {});
    api.get("/ponderr-learn/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  return (
    <div className="space-y-5">
      <div className="nb-card p-5 bg-[#93C5FD]">
        <div className="nb-label">PonderrLearn</div>
        <h2 className="display text-2xl font-bold">Reading Fluency · Art Studio · Immersive Notes</h2>
        <p className="text-sm mt-1">Author zero-cost, browser-native learning experiences and push them to students globally by board · grade · subject.</p>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {SUBTABS.map((t) => (
          <button key={t.id} onClick={() => setSub(t.id)}
            className={`nb-btn text-xs sm:text-sm shrink-0 ${sub === t.id ? "" : "nb-btn-ghost"}`}
            data-testid={`gpl-subtab-${t.id}`}><t.Icon size={14} />{t.label}</button>
        ))}
      </div>
      {sub === "read" && <ReadToLead boards={boards} grades={grades} settings={settings} />}
      {sub === "art" && <ArtPrompts boards={boards} grades={grades} />}
      {sub === "notes" && <NotesAuthor boards={boards} grades={grades} settings={settings} />}
      {sub === "settings" && <CoreSettings settings={settings} setSettings={setSettings} />}
    </div>
  );
}

function ReadToLead({ boards, grades, settings }) {
  const langs = settings.enabled_languages || [];
  const [f, setF] = useState({
    board: "", grade: "G01", subject_name: "English", language_code: "en-IN",
    passage_type: "PASSAGE", title: "", content_text: "", word_items: "",
    target_wpm: 90, max_marks: 10, publish_to_updates: false,
  });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/ponderr-learn/passages").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!f.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      const lang = langs.find((l) => l.code === f.language_code);
      await api.post("/ponderr-learn/passages", {
        ...f,
        language_label: lang?.label,
        word_items: f.passage_type === "WORD_LIST"
          ? f.word_items.split(/[,\n]/).map((w) => w.trim()).filter(Boolean) : [],
        content_text: f.passage_type === "PASSAGE" ? f.content_text : "",
        target_wpm: Number(f.target_wpm), max_marks: Number(f.max_marks),
      });
      toast.success("Pushed to students globally");
      setF({ ...f, title: "", content_text: "", word_items: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const del = async (id) => { await api.delete(`/ponderr-learn/passages/${id}`); load(); };
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="nb-card p-5 space-y-3">
        <div className="display text-lg font-bold">Create Reading Assignment</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Board">
            <select className="nb-select" value={f.board} onChange={(e) => setF({ ...f, board: e.target.value })} data-testid="gpl-read-board">
              <option value="">All Boards</option>
              {boards.map((b) => <option key={b.code} value={b.code}>{b.code}</option>)}
            </select>
          </Field>
          <Field label="Grade">
            <select className="nb-select" value={f.grade} onChange={(e) => setF({ ...f, grade: e.target.value })} data-testid="gpl-read-grade">
              {grades.map((g) => <option key={g.code} value={g.code}>{g.name}</option>)}
            </select>
          </Field>
          <Field label="Subject">
            <SubjectSelect value={f.subject_name} board={f.board} onChange={(v) => setF({ ...f, subject_name: v })} data-testid="gpl-read-subject" />
          </Field>
          <Field label="Language">
            <select className="nb-select" value={f.language_code} onChange={(e) => setF({ ...f, language_code: e.target.value })} data-testid="gpl-read-language">
              {langs.map((l) => <option key={l.code} value={l.code}>{l.label} ({l.code})</option>)}
            </select>
          </Field>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => setF({ ...f, passage_type: "PASSAGE" })}
            className={`nb-btn text-xs ${f.passage_type === "PASSAGE" ? "" : "nb-btn-ghost"}`} data-testid="gpl-read-type-passage">📄 Full Reading Passage</button>
          <button type="button" onClick={() => setF({ ...f, passage_type: "WORD_LIST" })}
            className={`nb-btn text-xs ${f.passage_type === "WORD_LIST" ? "" : "nb-btn-ghost"}`} data-testid="gpl-read-type-wordlist">🔤 Early Grade Word List</button>
        </div>
        <Field label="Title">
          <input className="nb-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="gpl-read-title" />
        </Field>
        {f.passage_type === "PASSAGE" ? (
          <Field label="Passage Text">
            <textarea className="nb-textarea" rows={5} value={f.content_text} onChange={(e) => setF({ ...f, content_text: e.target.value })} data-testid="gpl-read-content" />
          </Field>
        ) : (
          <Field label="Word List (comma or newline separated)">
            <textarea className="nb-textarea" rows={4} value={f.word_items} onChange={(e) => setF({ ...f, word_items: e.target.value })} placeholder="cat, dog, sun, moon" data-testid="gpl-read-words" />
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Target WPM"><input type="number" className="nb-input" value={f.target_wpm} onChange={(e) => setF({ ...f, target_wpm: e.target.value })} data-testid="gpl-read-wpm" /></Field>
          <Field label="Max Marks"><input type="number" className="nb-input" value={f.max_marks} onChange={(e) => setF({ ...f, max_marks: e.target.value })} data-testid="gpl-read-marks" /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.publish_to_updates} onChange={(e) => setF({ ...f, publish_to_updates: e.target.checked })} data-testid="gpl-read-publish" />
          Publish to Public Landing / Updates Feed
        </label>
        <button onClick={submit} disabled={busy} className="nb-btn w-full justify-center" data-testid="gpl-read-push">
          <Plus size={16} />{busy ? "Pushing…" : "Push to All Students Globally"}
        </button>
      </div>
      <div className="nb-card p-5 space-y-2">
        <div className="display text-lg font-bold">Published Reading Items</div>
        {rows.length === 0 && <div className="text-sm text-slate-500">Nothing yet.</div>}
        {rows.map((p) => (
          <div key={p.passage_id} className="nb-card-sm p-3 bg-[#FDE047] flex items-center justify-between gap-2" data-testid={`gpl-read-row-${p.passage_id}`}>
            <div className="min-w-0">
              <div className="font-bold truncate">{p.title}</div>
              <div className="text-xs mono">{p.board || "All"} · {p.grade} · {p.subject_name} · {p.language_label || p.language_code} · {p.passage_type}</div>
            </div>
            <button onClick={() => del(p.passage_id)} className="nb-btn nb-btn-danger text-xs" data-testid={`gpl-read-del-${p.passage_id}`}><Trash2 size={12} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ArtPrompts({ boards, grades }) {
  const [f, setF] = useState({ board: "", grade: "G01", subject_name: "Art", title: "", description: "", reference_image_url: "", submission_type: "BOTH", max_marks: 10, feature_on_landing: false });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const load = () => api.get("/ponderr-learn/art-prompts").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/ponderr-learn/files/upload", fd);
      setF((s) => ({ ...s, reference_image_url: r.data.storage_path }));
      toast.success("Reference image uploaded");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };
  const submit = async () => {
    if (!f.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      await api.post("/ponderr-learn/art-prompts", { ...f, max_marks: Number(f.max_marks) });
      toast.success("Art prompt pushed"); setF({ ...f, title: "", description: "", reference_image_url: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const del = async (id) => { await api.delete(`/ponderr-learn/art-prompts/${id}`); load(); };
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="nb-card p-5 space-y-3">
        <div className="display text-lg font-bold">Create Art & Craft Prompt</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Board">
            <select className="nb-select" value={f.board} onChange={(e) => setF({ ...f, board: e.target.value })} data-testid="gpl-art-board">
              <option value="">All Boards</option>
              {boards.map((b) => <option key={b.code} value={b.code}>{b.code}</option>)}
            </select>
          </Field>
          <Field label="Grade">
            <select className="nb-select" value={f.grade} onChange={(e) => setF({ ...f, grade: e.target.value })} data-testid="gpl-art-grade">
              {grades.map((g) => <option key={g.code} value={g.code}>{g.name}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Subject"><SubjectSelect value={f.subject_name} board={f.board} onChange={(v) => setF({ ...f, subject_name: v })} data-testid="gpl-art-subject" /></Field>
        <Field label="Title"><input className="nb-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="gpl-art-title" /></Field>
        <Field label="Instructions"><textarea className="nb-textarea" rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="gpl-art-desc" /></Field>
        <Field label="Reference Image">
          <label className="nb-btn nb-btn-ghost text-xs cursor-pointer inline-flex">
            <Upload size={14} />{uploading ? "Uploading…" : f.reference_image_url ? "Replace image" : "Upload image"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="gpl-art-refupload" />
          </label>
          {f.reference_image_url && <span className="ml-2 text-xs text-emerald-700">✓ attached</span>}
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Submission Type">
            <select className="nb-select" value={f.submission_type} onChange={(e) => setF({ ...f, submission_type: e.target.value })} data-testid="gpl-art-subtype">
              <option value="CANVAS">Canvas</option><option value="PHOTO">Photo</option><option value="BOTH">Both</option>
            </select>
          </Field>
          <Field label="Max Marks"><input type="number" className="nb-input" value={f.max_marks} onChange={(e) => setF({ ...f, max_marks: e.target.value })} data-testid="gpl-art-marks" /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.feature_on_landing} onChange={(e) => setF({ ...f, feature_on_landing: e.target.checked })} data-testid="gpl-art-feature" />
          Feature on Public Landing
        </label>
        <button onClick={submit} disabled={busy} className="nb-btn w-full justify-center" data-testid="gpl-art-push"><Plus size={16} />{busy ? "Pushing…" : "Push to Students"}</button>
      </div>
      <div className="nb-card p-5 space-y-2">
        <div className="display text-lg font-bold">Published Art Prompts</div>
        {rows.length === 0 && <div className="text-sm text-slate-500">Nothing yet.</div>}
        {rows.map((p) => (
          <div key={p.prompt_id} className="nb-card-sm p-3 bg-[#86EFAC] flex items-center justify-between gap-2" data-testid={`gpl-art-row-${p.prompt_id}`}>
            <div className="min-w-0"><div className="font-bold truncate">{p.title}</div>
              <div className="text-xs mono">{p.board || "All"} · {p.grade} · {p.subject_name} · {p.submission_type}</div></div>
            <button onClick={() => del(p.prompt_id)} className="nb-btn nb-btn-danger text-xs" data-testid={`gpl-art-del-${p.prompt_id}`}><Trash2 size={12} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function NotesAuthor({ boards, grades, settings }) {
  const langs = settings.enabled_languages || [];
  const [f, setF] = useState({ board: "", grade: "G01", subject_name: "English", title: "", body_html: "", audio_url: "", publish: true });
  const [translations, setTranslations] = useState([]);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/ponderr-learn/notes").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const addTr = () => setTranslations([...translations, { language_code: langs[1]?.code || "hi-IN", body_html: "" }]);
  const submit = async () => {
    if (!f.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      const trs = translations.map((t) => ({ ...t, language_label: langs.find((l) => l.code === t.language_code)?.label }));
      await api.post("/ponderr-learn/notes", { ...f, translations: trs });
      toast.success("Note published"); setF({ ...f, title: "", body_html: "" }); setTranslations([]); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const del = async (id) => { await api.delete(`/ponderr-learn/notes/${id}`); load(); };
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="nb-card p-5 space-y-3">
        <div className="display text-lg font-bold">Author Lesson Note</div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Board">
            <select className="nb-select" value={f.board} onChange={(e) => setF({ ...f, board: e.target.value })} data-testid="gpl-notes-board">
              <option value="">All</option>{boards.map((b) => <option key={b.code} value={b.code}>{b.code}</option>)}
            </select>
          </Field>
          <Field label="Grade">
            <select className="nb-select" value={f.grade} onChange={(e) => setF({ ...f, grade: e.target.value })} data-testid="gpl-notes-grade">
              {grades.map((g) => <option key={g.code} value={g.code}>{g.name}</option>)}
            </select>
          </Field>
          <Field label="Subject"><SubjectSelect value={f.subject_name} board={f.board} onChange={(v) => setF({ ...f, subject_name: v })} data-testid="gpl-notes-subject" /></Field>
        </div>
        <Field label="Title"><input className="nb-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="gpl-notes-title" /></Field>
        <Field label="Lesson Body (HTML allowed)"><textarea className="nb-textarea" rows={6} value={f.body_html} onChange={(e) => setF({ ...f, body_html: e.target.value })} data-testid="gpl-notes-body" /></Field>
        <Field label="Studio Audio URL (optional)"><input className="nb-input" value={f.audio_url} onChange={(e) => setF({ ...f, audio_url: e.target.value })} placeholder="https://…" data-testid="gpl-notes-audio" /></Field>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="nb-label">Translation Mirrors</span>
            <button type="button" onClick={addTr} className="nb-btn nb-btn-ghost text-xs" data-testid="gpl-notes-add-tr"><Plus size={12} />Add</button>
          </div>
          {translations.map((t, i) => (
            <div key={i} className="nb-card-sm p-3 bg-[#F1F5F9] space-y-2">
              <select className="nb-select text-xs" value={t.language_code} onChange={(e) => { const n = [...translations]; n[i].language_code = e.target.value; setTranslations(n); }} data-testid={`gpl-notes-tr-lang-${i}`}>
                {langs.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
              <textarea className="nb-textarea text-sm" rows={3} value={t.body_html} onChange={(e) => { const n = [...translations]; n[i].body_html = e.target.value; setTranslations(n); }} placeholder="Translated text" data-testid={`gpl-notes-tr-body-${i}`} />
            </div>
          ))}
        </div>
        <button onClick={submit} disabled={busy} className="nb-btn w-full justify-center" data-testid="gpl-notes-publish"><Save size={16} />{busy ? "Publishing…" : "Publish to Students"}</button>
      </div>
      <div className="nb-card p-5 space-y-2">
        <div className="display text-lg font-bold">Published Notes</div>
        {rows.length === 0 && <div className="text-sm text-slate-500">Nothing yet.</div>}
        {rows.map((n) => (
          <div key={n.note_id} className="nb-card-sm p-3 bg-[#C4B5FD] flex items-center justify-between gap-2" data-testid={`gpl-notes-row-${n.note_id}`}>
            <div className="min-w-0"><div className="font-bold truncate">{n.title}</div>
              <div className="text-xs mono">{n.board || "All"} · {n.grade} · {n.subject_name} · {(n.translations || []).length} mirror(s)</div></div>
            <button onClick={() => del(n.note_id)} className="nb-btn nb-btn-danger text-xs" data-testid={`gpl-notes-del-${n.note_id}`}><Trash2 size={12} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CoreSettings({ settings, setSettings }) {
  const [langs, setLangs] = useState(settings.enabled_languages || []);
  const [gender, setGender] = useState(settings.default_voice_gender || "female");
  const [minAcc, setMinAcc] = useState(settings.min_passing_accuracy ?? 70);
  const [nl, setNl] = useState({ code: "", label: "" });
  const [voiceName, setVoiceName] = useState(settings.default_voice_name || "");
  const [rate, setRate] = useState(settings.voice_rate ?? 0.9);
  const [pitch, setPitch] = useState(settings.voice_pitch ?? 1);
  const [voices, setVoices] = useState([]);
  useEffect(() => { loadVoices().then(() => setVoices(listBrowserVoices())); }, []);
  useEffect(() => {
    setLangs(settings.enabled_languages || []); setGender(settings.default_voice_gender || "female");
    setMinAcc(settings.min_passing_accuracy ?? 70); setVoiceName(settings.default_voice_name || "");
    setRate(settings.voice_rate ?? 0.9); setPitch(settings.voice_pitch ?? 1);
  }, [settings]);
  const preview = () => {
    setVoicePrefs({ default_voice_name: voiceName, default_voice_gender: gender, voice_rate: rate, voice_pitch: pitch });
    speak("Hello! This is how your reading voice will sound to students.", { lang: "en-IN", rate, pitch });
  };
  const save = async () => {
    try {
      await api.put("/ponderr-learn/settings", {
        enabled_languages: langs, default_voice_gender: gender, min_passing_accuracy: Number(minAcc),
        default_voice_name: voiceName, voice_rate: Number(rate), voice_pitch: Number(pitch),
      });
      const r = await api.get("/ponderr-learn/settings"); setSettings(r.data);
      toast.success("Settings saved");
    } catch { toast.error("Failed"); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="nb-card p-5 space-y-4">
        <div className="display text-lg font-bold">Language & Voice Manager</div>
        <div className="space-y-2">
          {langs.map((l, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`gpl-lang-${l.code}`}>
              <span className="nb-badge bg-[#93C5FD] mono">{l.code}</span>
              <span className="text-sm flex-1">{l.label}</span>
              <button onClick={() => setLangs(langs.filter((_, j) => j !== i))} className="nb-btn nb-btn-danger text-xs"><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input className="nb-input text-sm" placeholder="code e.g. bn-IN" value={nl.code} onChange={(e) => setNl({ ...nl, code: e.target.value })} data-testid="gpl-lang-code" />
          <input className="nb-input text-sm" placeholder="label e.g. Bengali" value={nl.label} onChange={(e) => setNl({ ...nl, label: e.target.value })} data-testid="gpl-lang-label" />
          <button onClick={() => { if (nl.code && nl.label) { setLangs([...langs, nl]); setNl({ code: "", label: "" }); } }} className="nb-btn text-xs" data-testid="gpl-lang-add"><Plus size={12} /></button>
        </div>
        <Field label="Default Voice Gender">
          <select className="nb-select" value={gender} onChange={(e) => setGender(e.target.value)} data-testid="gpl-voice-gender">
            <option value="female">Female</option><option value="male">Male</option>
          </select>
        </Field>
        <Field label={`Preferred Voice (${voices.length} available on this device)`}>
          <select className="nb-select" value={voiceName} onChange={(e) => setVoiceName(e.target.value)} data-testid="gpl-voice-name">
            <option value="">Auto (best match by language & gender)</option>
            {voices.map((v) => <option key={v.name} value={v.name}>{v.name} — {v.lang}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={`Speed (${Number(rate).toFixed(2)}x)`}>
            <input type="range" min="0.5" max="1.5" step="0.05" value={rate} onChange={(e) => setRate(e.target.value)} className="w-full" data-testid="gpl-voice-rate" />
          </Field>
          <Field label={`Pitch (${Number(pitch).toFixed(2)})`}>
            <input type="range" min="0" max="2" step="0.1" value={pitch} onChange={(e) => setPitch(e.target.value)} className="w-full" data-testid="gpl-voice-pitch" />
          </Field>
        </div>
        <button onClick={preview} className="nb-btn nb-btn-secondary w-full justify-center text-sm" data-testid="gpl-voice-preview"><Volume2 size={14} />Preview Voice</button>
        <Field label="Minimum Passing Accuracy (%)">
          <input type="number" className="nb-input" value={minAcc} onChange={(e) => setMinAcc(e.target.value)} data-testid="gpl-min-acc" />
        </Field>
        <button onClick={save} className="nb-btn w-full justify-center" data-testid="gpl-settings-save"><Save size={16} />Save Settings</button>
      </div>
      <DictionaryBank />
    </div>
  );
}

function DictionaryBank() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [f, setF] = useState({ keyword: "", language_code: "en-IN", definition: "", phonetic: "", svg_icon_url: "" });
  const load = (query) => api.get("/ponderr-learn/dictionary", { params: query ? { q: query } : {} }).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!f.keyword.trim()) return toast.error("Keyword required");
    try { await api.post("/ponderr-learn/dictionary", { ...f, examples: [] }); toast.success("Word added"); setF({ ...f, keyword: "", definition: "", phonetic: "", svg_icon_url: "" }); load(q); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/ponderr-learn/dictionary/${id}`); load(q); };
  return (
    <div className="nb-card p-5 space-y-3">
      <div className="display text-lg font-bold">Word & Picture Dictionary Bank</div>
      <div className="flex gap-2">
        <input className="nb-input text-sm" placeholder="Search words" value={q} onChange={(e) => setQ(e.target.value)} data-testid="gpl-dic-search" />
        <button onClick={() => load(q)} className="nb-btn text-xs" data-testid="gpl-dic-search-btn"><Search size={14} /></button>
      </div>
      <div className="nb-card-sm p-3 bg-[#FDE047] space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <input className="nb-input text-sm" placeholder="keyword" value={f.keyword} onChange={(e) => setF({ ...f, keyword: e.target.value })} data-testid="gpl-dic-keyword" />
          <input className="nb-input text-sm" placeholder="phonetic" value={f.phonetic} onChange={(e) => setF({ ...f, phonetic: e.target.value })} data-testid="gpl-dic-phonetic" />
        </div>
        <textarea className="nb-textarea text-sm" rows={2} placeholder="definition" value={f.definition} onChange={(e) => setF({ ...f, definition: e.target.value })} data-testid="gpl-dic-definition" />
        <input className="nb-input text-sm" placeholder="SVG / image icon URL (optional)" value={f.svg_icon_url} onChange={(e) => setF({ ...f, svg_icon_url: e.target.value })} data-testid="gpl-dic-icon" />
        <button onClick={add} className="nb-btn w-full justify-center text-sm" data-testid="gpl-dic-add"><Plus size={14} />Add Word</button>
      </div>
      <div className="space-y-1 max-h-72 overflow-y-auto">
        {rows.map((w) => (
          <div key={w.item_id} className="flex items-center gap-2 text-sm border-b border-black/10 py-1" data-testid={`gpl-dic-row-${w.item_id}`}>
            {w.svg_icon_url ? <img src={w.svg_icon_url} alt="" className="w-6 h-6" /> : <span className="w-6 h-6 grid place-items-center bg-[#93C5FD] rounded text-xs">📖</span>}
            <div className="flex-1 min-w-0"><b>{w.keyword}</b> <span className="text-xs mono text-slate-500">{w.phonetic}</span><div className="text-xs truncate">{w.definition}</div></div>
            <button onClick={() => del(w.item_id)} className="nb-btn nb-btn-danger text-xs"><Trash2 size={12} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (<label className="block"><span className="nb-label">{label}</span><div className="mt-1">{children}</div></label>);
}
